import { useId, useState } from 'react';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import type { SelectOption } from '../../types/generic';
import debounce from 'lodash.debounce';
import { getAllIntegrationAccounts } from '../../sources/tenant';
import FormGroup from './FormGroup';
import AsyncSelect from 'react-select/async';
import { getSharedStyles } from '../../utils/select';

interface Props {
    label: string;
    value: string | null;
    onChange: (accountId: string | null) => void;
    isRequired?: boolean;
    showValidation?: boolean;
    validationMessage?: string;
    className?: string;
}

const AccountDropdown = ({
    label,
    value,
    onChange,
    isRequired = false,
    showValidation = false,
    validationMessage = '',
    className = '',
}: Props) => {
    const dispatchNotification = useNotificationDispatch();

    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [account, setAccount] = useState<SelectOption | null>(null);

    const accountInputId = useId();

    const loadAccountOptions = debounce(
        async (_: string, callback: (options: SelectOption[]) => void) => {
            try {
                setLoadingAccounts(true);

                const results = await getAllIntegrationAccounts();
                const options: SelectOption[] = results.map<SelectOption>((accountId) => ({
                    value: accountId,
                    label: accountId,
                }));

                setAccount(options.find((option) => option.value === value) || null);

                callback(options);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setLoadingAccounts(false);
            }
        },
        500,
    ) as (inputValue: string, callback: (options: SelectOption[]) => void) => void;

    return (
        <FormGroup
            label={label}
            isRequired={isRequired}
            isValid={!!account}
            showValidation={showValidation}
            validationMessage={validationMessage}
            htmlFor={accountInputId}
            className={className}
        >
            <AsyncSelect
                inputId={accountInputId}
                isMulti={false}
                isSearchable={false}
                className="margin-bottom-small"
                loadOptions={loadAccountOptions}
                onChange={(newValue) => {
                    setAccount(newValue);
                    onChange(newValue?.value || null);
                }}
                value={account}
                isLoading={loadingAccounts}
                defaultOptions
                styles={getSharedStyles<SelectOption>()}
            />
        </FormGroup>
    );
};

export default AccountDropdown;
